import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-ios-fairplay-player',
  templateUrl: './ios-fairplay-player.component.html',
  styleUrls: ['./ios-fairplay-player.component.css'],
})
export class IosFairplayPlayerComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild('videoEl', { static: true })
  videoElRef!: ElementRef<HTMLVideoElement>;
  @Input() playbackUrl: string = '';
  @Input() licenseServerUrl: string = '';
  @Input() certificateUrl: string = '';
  @Input() token: string = '';
  @Input() poster: string = '';
  @Input() startTime: number = 0;
  @Input() showDebug: boolean = false;
  @Output() playing = new EventEmitter<void>();
  @Output() ended = new EventEmitter<void>();
  @Output() error = new EventEmitter<any>();
  @Output() timeUpdate = new EventEmitter<number>();
  isLoading = true;
  errorMessage = '';
  debugLog = '';
  currentTime: number = 0;
  duration: number = 0;
  sessionWatchTime: number = 0;
  private _watchTimerStart: number | null = null;
  private video!: HTMLVideoElement;
  private destroyed = false;
  private fpCertificate: ArrayBuffer | null = null;
  private keySession: MediaKeySession | null = null;
  private onEncryptedBound = (e: MediaEncryptedEvent) => this.onEncrypted(e);
  private onVideoPlayingBound = () => this.handlePlaying();
  private onVideoPauseBound = () => this.stopWatchTimer();
  private onVideoEndedBound = () => this.handleEnded();
  private onVideoErrorBound = () => this.handleVideoError();
  private onVideoTimeUpdateBound = () => this.handleTimeUpdate();
  isLandscape:boolean = false;

  ngAfterViewInit(): void {
    this.video = this.videoElRef.nativeElement;
    this.attachVideoEvents();
    this.attachFullscreenListeners();
    this.init().catch((err) => this.fail(err));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['playbackUrl'] && !changes['playbackUrl'].firstChange) {
      this.retry();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopWatchTimer();
    this.detachVideoEvents();
    this.cleanup();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
  }

  retry(): void {
    this.log('🔁 Retry triggered');
    this.errorMessage = '';
    this.isLoading = true;

    this.cleanup();
    this.init().catch((err) => this.fail(err));
  }

  private async init(): Promise<void> {
    if (!this.playbackUrl) throw new Error('playbackUrl missing');
    if (!this.licenseServerUrl) throw new Error('licenseServerUrl missing');
    if (!this.certificateUrl) throw new Error('certificateUrl missing');
    if (!this.token) throw new Error('token missing');
    if (!this.isSafariApple()) {
      throw new Error('FairPlay supported only on Safari Apple devices');
    }
    this.log('✅ Initializing FairPlay (Axinom POC style - EME)');
    this.log(`🎬 HLS: ${this.playbackUrl}`);
    this.log(`🔑 License: ${this.licenseServerUrl}`);
    this.log(`📄 Cert: ${this.certificateUrl}`);
    await this.loadCertificate();
    const url = this.playbackUrl.includes('?') ? `${this.playbackUrl}` : `${this.playbackUrl}`;
    this.video.src = url;
    this.video.load();
    this.video.addEventListener(
      'loadedmetadata',
      async () => {
        try {
          if (this.startTime > 0) {
            this.video.currentTime = this.startTime;
            this.log(`⏩ Resuming from ${this.startTime}s`);
          }
          this.log('🎬 loadedmetadata → attempting play()');
          await this.video.play();
          this.log('▶️ play() succeeded');
        } catch (e) {
          this.log('⚠️ autoplay blocked — user interaction required');
        }
      },
      { once: true }
    );
    this.isLoading = false;
    this.log('🟢 Ready. Waiting for DRM + playback');
  }

  private async loadCertificate(): Promise<void> {
    if (this.fpCertificate) return;
    this.log(`📄 Fetching certificate...`);
    const resp = await fetch(this.certificateUrl, { mode: 'cors' });
    if (!resp.ok) throw new Error(`Certificate fetch failed: ${resp.status}`);
    this.fpCertificate = await resp.arrayBuffer();
    this.log(`✅ Certificate loaded (${this.fpCertificate.byteLength} bytes)`);
  }

  private async onEncrypted(event: MediaEncryptedEvent): Promise<void> {
    if (this.destroyed) return;
    try {
      const initDataType = event.initDataType;
      this.log(`🔐 Encrypted event received initDataType=${initDataType}`);
      if (initDataType !== 'skd') {
        this.log(`⚠️ Ignored initDataType=${initDataType}`);
        return;
      }
      const initData = event.initData;
      if (!initData) throw new Error('No initData found');
      if (!this.video.mediaKeys) {
        await this.setupMediaKeys();
      }
      if (!this.video.mediaKeys) throw new Error('MediaKeys missing');
      if (!this.keySession) {
        this.keySession = this.video.mediaKeys.createSession('temporary');
        this.keySession.addEventListener('message', async (msgEvt: any) => {
          try {
            const spcBytes: ArrayBuffer = msgEvt.message;
            this.log(`📨 SPC received (${spcBytes.byteLength} bytes)`);
            const ckc = await this.requestLicense(spcBytes);
            this.log(`📩 CKC received (${ckc.byteLength} bytes)`);
            await this.keySession!.update(ckc);
            this.log('✅ session.update success');
          } catch (e) {
            this.fail(e);
          }
        });
      }
      const assetId = this.extractAssetId(initData);
      await this.keySession.generateRequest('skd', assetId);
      this.log('✅ generateRequest(assetId) done');
    } catch (e) {
      this.fail(e);
    }
  }

  private async setupMediaKeys(): Promise<void> {
    this.log('🎟️ Requesting FairPlay key system access...')
    const config: MediaKeySystemConfiguration[] = [
      {
        initDataTypes: ['skd'],
        videoCapabilities: [{ contentType: 'video/mp4', robustness: '' }],
        distinctiveIdentifier: 'not-allowed',
        persistentState: 'not-allowed',
        sessionTypes: ['temporary'],
      },
    ];
    let access: MediaKeySystemAccess;
    try {
      access = await navigator.requestMediaKeySystemAccess('com.apple.fps.1_0', config);
      this.log('✅ access com.apple.fps.1_0 granted');
    } catch {
      access = await navigator.requestMediaKeySystemAccess('com.apple.fps', config);
      this.log('✅ access com.apple.fps granted');
    }
    const keys = await access.createMediaKeys();
    await keys.setServerCertificate(this.fpCertificate!);
    await this.video.setMediaKeys(keys);
    this.log('✅ MediaKeys set + certificate attached');
  }

  private extractAssetId(initData: ArrayBuffer): ArrayBuffer {
    const data = new Uint8Array(initData);
    let str = '';
    for (let i = 0; i < data.length; i++) {
      if (data[i] === 0) continue;
      str += String.fromCharCode(data[i]);
    }
    const skdIndex = str.indexOf('skd://');
    if (skdIndex === -1) {
      throw new Error('No skd:// found in initData');
    }
    const tail = str.substring(skdIndex);
    const end = tail.search(/[\s'"]/);
    const skdUri = end === -1 ? tail.trim() : tail.substring(0, end).trim();
    this.log(`🎯 Extracted SKD URI: ${skdUri}`);
    return new TextEncoder().encode(skdUri).buffer;
  }

  private async requestLicense(spcBytes: ArrayBuffer): Promise<ArrayBuffer> {
    this.log(`➡️ POST SPC to Axinom AcquireLicense`);
    const resp = await fetch(this.licenseServerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-AxDRM-Message': this.token, 
      },
      body: spcBytes,
    });
    if (!resp.ok) {
      const drmErr = resp.headers.get('X-AxDRM-ErrorMessage') || '';
      throw new Error(`License failed: HTTP ${resp.status} ${drmErr}`);
    }
    return await resp.arrayBuffer();
  }

  private attachVideoEvents(): void {
    this.video.addEventListener('encrypted', this.onEncryptedBound as any);
    this.video.addEventListener('playing', this.onVideoPlayingBound);
    this.video.addEventListener('pause', this.onVideoPauseBound);
    this.video.addEventListener('ended', this.onVideoEndedBound);
    this.video.addEventListener('error', this.onVideoErrorBound);
    this.video.addEventListener('timeupdate', this.onVideoTimeUpdateBound);
    this.video.addEventListener('loadedmetadata', () => {
      this.duration = this.video.duration || 0;
    });
  }

  private detachVideoEvents(): void {
    if (!this.video) return;
    this.video.removeEventListener('encrypted', this.onEncryptedBound as any);
    this.video.removeEventListener('playing', this.onVideoPlayingBound);
    this.video.removeEventListener('pause', this.onVideoPauseBound);
    this.video.removeEventListener('ended', this.onVideoEndedBound);
    this.video.removeEventListener('error', this.onVideoErrorBound);
    this.video.removeEventListener('timeupdate', this.onVideoTimeUpdateBound);
  }

  private cleanup(): void {
    try {
      if (this.keySession) {
        try { this.keySession.close(); } catch {}
      }
      this.keySession = null;
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
    } catch {}
  }

  private handlePlaying(): void {
    this.isLoading = false;
    this.errorMessage = '';
    this.startWatchTimer();
    this.playing.emit();
    this.log('▶️ video playing');
  }

  private handleEnded(): void {
    this.stopWatchTimer();
    this.ended.emit();
    this.log('✅ video ended');
  }

  private handleTimeUpdate(): void {
    this.currentTime = this.video.currentTime;
    this.timeUpdate.emit(this.currentTime);
  }

  private startWatchTimer(): void {
    if (!this._watchTimerStart) {
      this._watchTimerStart = Date.now();
    }
  }

  private stopWatchTimer(): void {
    if (this._watchTimerStart) {
      this.sessionWatchTime += Date.now() - this._watchTimerStart;
      this._watchTimerStart = null;
    }
  }

  flushSessionWatchTime(): number {
    this.stopWatchTimer();
    return this.sessionWatchTime;
  }

  private handleVideoError(): void {
    const err: any = (this.video as any)?.error;
    const msg = `Video error code=${err?.code ?? 'n/a'} message=${err?.message ?? 'n/a'}`;
    this.fail(new Error(msg));
  }

  private fail(err: any): void {
    console.error('❌ FairPlay Player Error:', err);
    this.error.emit(err);

    if (!this.destroyed) {
      this.isLoading = false;
      this.errorMessage = err?.message || 'Playback failed';
    }

    this.log(`❌ FAIL: ${err?.message || err}`);
  }

  private log(msg: string): void {
    console.log(msg);
    this.debugLog += `${msg}\n`;
  }

  private isSafariApple(): boolean {
    const ua = navigator.userAgent;
    const isApple = /iPad|iPhone|iPod|Macintosh/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isApple && isSafari;
  }

  private onFullscreenChange = () => {
    const doc: any = document;
    const isFs = doc.fullscreenElement || doc.webkitFullscreenElement || (this.video as any).webkitDisplayingFullscreen;
    this.isLandscape = !!isFs;   
    if (this.isLandscape) {
      document.body.classList.add('ios-landscape');
      this.log('📱 Entered fullscreen → landscape mode');
    } else {
      document.body.classList.remove('ios-landscape');
      this.log('📱 Exited fullscreen → portrait mode');
    }
  };

  private attachFullscreenListeners() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    this.video.addEventListener('webkitbeginfullscreen', this.onFullscreenChange);
    this.video.addEventListener('webkitendfullscreen', this.onFullscreenChange);
  }
}